import React, { useEffect, useState } from 'react'
import { ColorSelectorV2, SelectV2 } from '@labrav/react-components'
import { FormikProps, useFormikContext } from 'formik'
import { SelectChangeEvent, Typography } from '@mui/material'
import clsx from 'clsx'
import {
  DataType,
  FormField,
} from '@labrav/react-components/lib/@types/types/forms'
import { useStyles } from './ButtonsTypesForm.styles'
import {
  extractColorV2,
  hexadecimalColor,
} from '../../../../../../common/utils/productCreationUtil'
import { calculateMinMaxResult } from '../../../../../AddEditProductFormComponents/FlyOutProductBranding/FlyOutProductBranding'
import { convertRGBAAlpha } from '../../../../../../common/utils/convertRGBAAlpha'
import {
  fontSizes,
  fontWeights,
} from '../../../../../../utils/templateCreationConstants'
import { hexToRgb, rgbToHex } from '../../../../../../utils/colorsConvertor'
import { ListingCardInCreation } from '../../../../../../modules/storefront/listingCardCreation/reducer'

export const ButtonsTypesForm: React.FC = () => {
  const { classes } = useStyles()
  const { values, errors, touched, handleBlur, setFieldValue, handleChange } =
    useFormikContext() as FormikProps<ListingCardInCreation>
  const { listingCardData } = values
  useEffect(() => {
    if (values?.listingCardData?.template?.data?.buttons) {
      const updatedButtons =
        values?.listingCardData?.template?.data?.buttons.map(button => {
          const customizationType =
            button.fieldType === 'Filled' ? 'filledButton' : 'outlinedButton'

          return button.visibility &&
            (button.fieldType === 'Filled' || button.fieldType === 'Outlined')
            ? {
                ...button,
                customization: {
                  ...button.customization,
                  ...listingCardData?.template?.data[customizationType],
                },
              }
            : button
        })

      setFieldValue('listingCardData.template.data.buttons', updatedButtons)
    }
  }, [
    listingCardData?.template?.data?.filledButton,
    listingCardData?.template?.data?.outlinedButton,
  ])

  const [filledTextColorType, setFilledTextColorType] = useState<string>(
    listingCardData?.template?.data?.filledButton?.color
      ? listingCardData?.template?.data?.filledButton?.color?.includes('#')
        ? 'HEX'
        : 'RGB'
      : 'HEX'
  )

  const [filledTextColor, setFilledTextColor] = useState<string[]>(() => {
    const colorString =
      listingCardData?.template?.data?.filledButton?.color || ''
    return extractColorV2(filledTextColorType, colorString)
  })

  useEffect(() => {
    const colorString =
      listingCardData?.template?.data?.filledButton?.color || ''
    setFilledTextColor(extractColorV2(filledTextColorType, colorString))
  }, [listingCardData?.template?.data?.filledButton?.color])

  const [filledBgColorType, setFilledBgColorType] = useState<string>(
    listingCardData?.template?.data?.filledButton?.backgroundColor
      ? listingCardData?.template?.data?.filledButton?.backgroundColor?.includes(
          '#'
        )
        ? 'HEX'
        : 'RGB'
      : 'HEX'
  )

  const [filledBgColor, setFilledBgColor] = useState<string[]>(() => {
    const colorString =
      listingCardData?.template?.data?.filledButton?.backgroundColor || ''
    return extractColorV2(filledBgColorType, colorString)
  })

  useEffect(() => {
    const colorString =
      listingCardData?.template?.data?.filledButton?.backgroundColor || ''
    setFilledBgColor(extractColorV2(filledBgColorType, colorString))
  }, [listingCardData?.template?.data?.filledButton?.backgroundColor])

  const [outlinedTextColorType, setOutlinedTextColorType] = useState<string>(
    listingCardData?.template?.data?.outlinedButton?.color
      ? listingCardData.template.data.outlinedButton?.color?.includes('#')
        ? 'HEX'
        : 'RGB'
      : 'HEX'
  )

  const [outlinedTextColor, setOutlinedTextColor] = useState<string[]>(() => {
    const colorString =
      listingCardData?.template?.data?.outlinedButton?.color || ''
    return extractColorV2(outlinedTextColorType, colorString)
  })

  useEffect(() => {
    const colorString =
      listingCardData?.template?.data?.outlinedButton?.color || ''
    setOutlinedTextColor(extractColorV2(outlinedTextColorType, colorString))
  }, [listingCardData?.template?.data?.outlinedButton?.color])

  const [outlinedBgColorType, setOutlinedBgColorType] = useState<string>(
    listingCardData?.template?.data?.outlinedButton?.borderColor
      ? listingCardData.template.data.outlinedButton?.borderColor?.includes('#')
        ? 'HEX'
        : 'RGB'
      : 'HEX'
  )

  const [outlinedBgColor, setOutlinedBgColor] = useState<string[]>(() => {
    const colorString =
      listingCardData?.template?.data?.outlinedButton?.borderColor || ''
    return extractColorV2(outlinedBgColorType, colorString)
  })

  useEffect(() => {
    const colorString =
      listingCardData?.template?.data?.outlinedButton?.borderColor || ''
    setOutlinedBgColor(extractColorV2(outlinedBgColorType, colorString))
  }, [listingCardData?.template?.data?.outlinedButton?.borderColor])

  const handleTypeChange = async (event: SelectChangeEvent, type: string) => {
    const typeValue = event.target.value as string
    const updatedButtons = listingCardData?.template?.data?.buttons?.map(
      button => {
        if (button.visibility) {
          if (type === 'FILLED_TEXT_COLOR' && button.fieldType === 'Filled') {
            const resetValue =
              typeValue === 'RGB'
                ? hexToRgb(filledTextColor[4]).toString()
                : rgbToHex(
                    parseInt(filledTextColor[0]),
                    parseInt(filledTextColor[1]),
                    parseInt(filledTextColor[2])
                  )
            return {
              ...button,
              customization: {
                ...button.customization,
                color: resetValue,
              },
            }
          } else if (
            type === 'FILLED_BG_COLOR' &&
            button.fieldType === 'Filled'
          ) {
            const resetValue =
              typeValue === 'RGB'
                ? hexToRgb(filledBgColor[4]).toString()
                : rgbToHex(
                    parseInt(filledBgColor[0]),
                    parseInt(filledBgColor[1]),
                    parseInt(filledBgColor[2])
                  )
            return {
              ...button,
              customization: {
                ...button.customization,
                backgroundColor: resetValue,
              },
            }
          } else if (
            type === 'OUTLINED_TEXT_COLOR' &&
            button.fieldType === 'Outlined'
          ) {
            const resetValue =
              typeValue === 'RGB'
                ? hexToRgb(outlinedTextColor[4]).toString()
                : rgbToHex(
                    parseInt(outlinedTextColor[0]),
                    parseInt(outlinedTextColor[1]),
                    parseInt(outlinedTextColor[2])
                  )
            return {
              ...button,
              customization: {
                ...button.customization,
                color: resetValue,
              },
            }
          } else if (
            type === 'OUTLINED_BG_COLOR' &&
            button.fieldType === 'Outlined'
          ) {
            const resetValue =
              typeValue === 'RGB'
                ? hexToRgb(outlinedBgColor[4]).toString()
                : rgbToHex(
                    parseInt(outlinedBgColor[0]),
                    parseInt(outlinedBgColor[1]),
                    parseInt(outlinedBgColor[2])
                  )
            return {
              ...button,
              customization: {
                ...button.customization,
                borderColor: resetValue,
              },
            }
          }
        }
        return button
      }
    )

    await setFieldValue('listingCardData.template.data.buttons', updatedButtons)

    switch (type) {
      case 'FILLED_TEXT_COLOR': {
        const resetValue =
          typeValue === 'RGB'
            ? hexToRgb(filledTextColor[4]).toString()
            : rgbToHex(
                parseInt(filledTextColor[0]),
                parseInt(filledTextColor[1]),
                parseInt(filledTextColor[2])
              )
        await setFilledTextColorType(typeValue)
        await setFieldValue(
          'listingCardData.template.data.filledButton.color',
          resetValue
        )
        break
      }
      case 'FILLED_BG_COLOR': {
        const resetValue =
          typeValue === 'RGB'
            ? hexToRgb(filledBgColor[4]).toString()
            : rgbToHex(
                parseInt(filledBgColor[0]),
                parseInt(filledBgColor[1]),
                parseInt(filledBgColor[2])
              )
        await setFilledBgColorType(typeValue)
        await setFieldValue(
          'listingCardData.template.data.filledButton.backgroundColor',
          resetValue
        )
        break
      }
      case 'OUTLINED_TEXT_COLOR': {
        const resetValue =
          typeValue === 'RGB'
            ? hexToRgb(outlinedTextColor[4]).toString()
            : rgbToHex(
                parseInt(outlinedTextColor[0]),
                parseInt(outlinedTextColor[1]),
                parseInt(outlinedTextColor[2])
              )
        await setOutlinedTextColorType(typeValue)
        await setFieldValue(
          'listingCardData.template.data.outlinedButton.color',
          resetValue
        )
        break
      }
      case 'OUTLINED_BG_COLOR': {
        const resetValue =
          typeValue === 'RGB'
            ? hexToRgb(outlinedBgColor[4]).toString()
            : rgbToHex(
                parseInt(outlinedBgColor[0]),
                parseInt(outlinedBgColor[1]),
                parseInt(outlinedBgColor[2])
              )
        await setOutlinedBgColorType(typeValue)
        await setFieldValue(
          'listingCardData.template.data.outlinedButton.borderColor',
          resetValue
        )
        break
      }
      default:
        break
    }
  }

  const handleColorChange = async (
    index: number,
    event: SelectChangeEvent,
    type: string
  ) => {
    const updatedValue = event.target.value as string

    switch (type) {
      case 'FILLED_TEXT_COLOR': {
        const updatedColorValues = calculateMinMaxResult(
          index,
          [...filledTextColor],
          updatedValue
        )
        const updatedColor =
          filledTextColorType === 'RGB'
            ? `rgba(${updatedColorValues[0]}, ${updatedColorValues[1]}, ${updatedColorValues[2]}, ${updatedColorValues[3]})`
            : hexadecimalColor(
                `${updatedColorValues[4]}`,
                Number(updatedColorValues[3])
              )

        setFilledTextColor(updatedColorValues)
        await setFieldValue(
          'listingCardData.template.data.filledButton.color',
          updatedColor
        )

        const updatedButtons = listingCardData?.template?.data?.buttons?.map(
          button => {
            if (button.visibility && button.fieldType === 'Filled') {
              return {
                ...button,
                customization: {
                  ...button.customization,
                  color: updatedColor,
                },
              }
            }
            return button
          }
        )
        await setFieldValue(
          'listingCardData.template.data.buttons',
          updatedButtons
        )
        break
      }

      case 'FILLED_BG_COLOR': {
        const updatedColorValues = calculateMinMaxResult(
          index,
          [...filledBgColor],
          updatedValue
        )
        const updatedColor =
          filledBgColorType === 'RGB'
            ? `rgba(${updatedColorValues[0]}, ${updatedColorValues[1]}, ${updatedColorValues[2]}, ${updatedColorValues[3]})`
            : hexadecimalColor(
                `${updatedColorValues[4]}`,
                Number(updatedColorValues[3])
              )

        setFilledBgColor(updatedColorValues)
        await setFieldValue(
          'listingCardData.template.data.filledButton.backgroundColor',
          updatedColor
        )

        const updatedButtons = listingCardData?.template?.data?.buttons?.map(
          button => {
            if (button.visibility && button.fieldType === 'Filled') {
              return {
                ...button,
                customization: {
                  ...button.customization,
                  backgroundColor: updatedColor,
                },
              }
            }
            return button
          }
        )
        await setFieldValue(
          'listingCardData.template.data.buttons',
          updatedButtons
        )
        break
      }

      case 'OUTLINED_TEXT_COLOR': {
        const updatedColorValues = calculateMinMaxResult(
          index,
          [...outlinedTextColor],
          updatedValue
        )
        const updatedColor =
          outlinedTextColorType === 'RGB'
            ? `rgba(${updatedColorValues[0]}, ${updatedColorValues[1]}, ${updatedColorValues[2]}, ${updatedColorValues[3]})`
            : hexadecimalColor(
                `${updatedColorValues[4]}`,
                Number(updatedColorValues[3])
              )

        setOutlinedTextColor(updatedColorValues)
        await setFieldValue(
          'listingCardData.template.data.outlinedButton.color',
          updatedColor
        )

        const updatedButtons = listingCardData?.template?.data?.buttons?.map(
          button => {
            if (button.visibility && button.fieldType === 'Outlined') {
              return {
                ...button,
                customization: {
                  ...button.customization,
                  color: updatedColor,
                },
              }
            }
            return button
          }
        )
        await setFieldValue(
          'listingCardData.template.data.buttons',
          updatedButtons
        )
        break
      }

      case 'OUTLINED_BG_COLOR': {
        const updatedColorValues = calculateMinMaxResult(
          index,
          [...outlinedBgColor],
          updatedValue
        )
        const updatedColor =
          outlinedBgColorType === 'RGB'
            ? `rgba(${updatedColorValues[0]}, ${updatedColorValues[1]}, ${updatedColorValues[2]}, ${updatedColorValues[3]})`
            : hexadecimalColor(
                `${updatedColorValues[4]}`,
                Number(updatedColorValues[3])
              )

        setOutlinedBgColor(updatedColorValues)
        await setFieldValue(
          'listingCardData.template.data.outlinedButton.borderColor',
          updatedColor
        )

        const updatedButtons = listingCardData?.template?.data?.buttons?.map(
          button => {
            if (button.visibility && button.fieldType === 'Outlined') {
              return {
                ...button,
                customization: {
                  ...button.customization,
                  borderColor: updatedColor,
                },
              }
            }
            return button
          }
        )
        await setFieldValue(
          'listingCardData.template.data.buttons',
          updatedButtons
        )
        break
      }

      default:
        break
    }
  }

  const filledFontSizeField = generateOptions(
    'listingCardData.template.data.filledButton.fontSize',
    fontSizes,
    'Font size',
    'Font size',
    'selectFontSize',
    values?.listingCardData?.template?.data?.filledButton?.fontSize
  )

  const filledFontWeightField = generateOptions(
    'listingCardData.template.data.filledButton.fontWeight',
    fontWeights,
    'Font weight',
    'Font weight',
    'selectFontWeight',
    values?.listingCardData?.template?.data?.filledButton?.fontWeight
  )
  const outlinedFontSizeField = generateOptions(
    'listingCardData.template.data.outlinedButton.fontSize',
    fontSizes,
    'Font size',
    'Font size',
    'selectFontSize',
    values?.listingCardData?.template?.data?.outlinedButton?.fontSize
  )

  const outlinedFontWeightField = generateOptions(
    'listingCardData.template.data.outlinedButton.fontWeight',
    fontWeights,
    'Font weight',
    'Font weight',
    'selectFontWeight',
    values?.listingCardData?.template?.data?.outlinedButton?.fontWeight
  )
  return (
    <div className={clsx(classes.sectionWrapper)} data-testid="buttons-wrapper">
      <div
        className={clsx(
          classes.buttonFormSection,
          classes.rootComp,
          classes.content
        )}
        data-testid="filled-buttons-form"
      >
        <Typography className={classes.headerTitle} data-testid="filled-title">
          Filled button (Primary button)
        </Typography>
        <div
          className={classes.formFieldsContainer}
          data-testid="selector-filled-wrapper"
        >
          <Typography className={classes.subTitle}>Text</Typography>
          <div className={classes.feildsForm}>
            <div className={classes.colorSelectorWidth}>
              <ColorSelectorV2
                data-testid="color-selector-filled-text"
                selectedField={filledTextColorType}
                placeholder={'#FFFFFF'}
                onBlur={handleBlur}
                keyName={'listingCardData.template.data.filledButton.color'}
                colorValues={filledTextColor}
                color={convertRGBAAlpha(
                  listingCardData?.template?.data?.filledButton?.color || '',
                  false
                )}
                onChange={event => handleTypeChange(event, 'FILLED_TEXT_COLOR')}
                handleColorChange={(index, event) =>
                  handleColorChange(index, event, 'FILLED_TEXT_COLOR')
                }
                isError={
                  !!(
                    (touched as any).listingCardData?.template?.data
                      ?.filledButton?.color &&
                    (errors as any).listingCardData?.template?.data
                      ?.filledButton?.color
                  )
                }
                errorText={
                  !!(touched as any).listingCardData?.template?.data
                    ?.filledButton?.color &&
                  ((errors as any).listingCardData?.template?.data?.filledButton
                    ?.color ||
                    '')
                }
                secondaryVariant={true}
              />
            </div>
            <div
              className={classes.selectorWidth}
              data-testid="font-size-selector"
            >
              <SelectV2
                field={{ ...filledFontSizeField }}
                value={
                  values?.listingCardData?.template?.data?.filledButton
                    ?.fontSize
                }
                onChange={handleChange}
                onBlur={handleBlur}
                minWidth="119px"
              />
            </div>
            <div
              className={classes.selectorWidth}
              data-testid="font-weight-selector"
            >
              <SelectV2
                field={{ ...filledFontWeightField }}
                value={
                  values?.listingCardData?.template?.data?.filledButton
                    ?.fontWeight
                }
                onChange={handleChange}
                onBlur={handleBlur}
                minWidth="119px"
              />
            </div>
          </div>
        </div>
        <div className={classes.colorSelectorWrapper}>
          <Typography className={classes.subTitle}>Background color</Typography>
          <div className={classes.colorSelectorWidth}>
            <ColorSelectorV2
              selectedField={filledBgColorType}
              placeholder={'#FFFFFF'}
              onBlur={handleBlur}
              keyName={
                'listingCardData.template.data.filledButton.backgroundColor'
              }
              colorValues={filledBgColor}
              color={convertRGBAAlpha(
                listingCardData?.template?.data?.filledButton
                  ?.backgroundColor || '',
                false
              )}
              onChange={event => handleTypeChange(event, 'FILLED_BG_COLOR')}
              handleColorChange={(index, event) =>
                handleColorChange(index, event, 'FILLED_BG_COLOR')
              }
              isError={
                !!(
                  (touched as any).listingCardData?.template?.data?.filledButton
                    ?.backgroundColor &&
                  (errors as any).listingCardData?.template?.data?.filledButton
                    ?.backgroundColor
                )
              }
              errorText={
                !!(touched as any).listingCardData?.template?.data?.filledButton
                  ?.backgroundColor &&
                ((errors as any).listingCardData?.template?.data?.filledButton
                  ?.backgroundColor ||
                  '')
              }
              secondaryVariant={true}
            />
          </div>
        </div>
      </div>
      {/* secondary button */}
      <div
        className={clsx(
          classes.buttonFormSection,
          classes.rootComp,
          classes.content
        )}
        data-testid="outlined-wrapper"
      >
        <Typography
          className={classes.headerTitle}
          data-testid="outlined-title"
        >
          Outlined button (Secondary button)
        </Typography>
        <div
          className={classes.formFieldsContainer}
          data-testid="outlined-selector-wrapper"
        >
          <Typography className={classes.subTitle}>Text</Typography>
          <div className={classes.feildsForm}>
            <div className={classes.colorSelectorWidth}>
              <ColorSelectorV2
                selectedField={outlinedTextColorType}
                placeholder={'#FFFFFF'}
                onBlur={handleBlur}
                keyName={'listingCardData.template.data.outlinedButton.color'}
                colorValues={outlinedTextColor}
                color={convertRGBAAlpha(
                  listingCardData?.template?.data?.outlinedButton?.color || '',
                  false
                )}
                onChange={event =>
                  handleTypeChange(event, 'OUTLINED_TEXT_COLOR')
                }
                handleColorChange={(index, event) =>
                  handleColorChange(index, event, 'OUTLINED_TEXT_COLOR')
                }
                isError={
                  !!(
                    (touched as any).listingCardData?.template?.data
                      ?.outlinedButton?.color &&
                    (errors as any).listingCardData?.template?.data
                      ?.outlinedButton?.color
                  )
                }
                errorText={
                  !!(touched as any).listingCardData?.template?.data
                    ?.outlinedButton?.color &&
                  ((errors as any).listingCardData?.template?.data
                    ?.outlinedButton?.color ||
                    '')
                }
                secondaryVariant={true}
              />
            </div>

            <div
              className={classes.selectorWidth}
              data-testid="font-size-selector-outlined"
            >
              <SelectV2
                field={{ ...outlinedFontSizeField }}
                value={
                  values?.listingCardData?.template?.data?.outlinedButton
                    ?.fontSize
                }
                onChange={handleChange}
                onBlur={handleBlur}
                minWidth="119px"
              />
            </div>
            <div
              className={classes.selectorWidth}
              data-testid="font-weight-selector-outlined"
            >
              <SelectV2
                field={{ ...outlinedFontWeightField }}
                value={
                  values?.listingCardData?.template?.data?.outlinedButton
                    ?.fontWeight
                }
                onChange={handleChange}
                onBlur={handleBlur}
                minWidth="119px"
              />
            </div>
          </div>
        </div>
        <div className={classes.colorSelectorWrapper}>
          <Typography className={classes.subTitle}>
            Button stroke color
          </Typography>
          <div className={classes.colorSelectorWidth}>
            <ColorSelectorV2
              selectedField={outlinedBgColorType}
              placeholder={'#FFFFFF'}
              onBlur={handleBlur}
              keyName={
                'listingCardData.template.data.outlinedButton.borderColor'
              }
              colorValues={outlinedBgColor}
              color={convertRGBAAlpha(
                listingCardData?.template?.data?.outlinedButton?.borderColor ||
                  '',
                false
              )}
              onChange={event => handleTypeChange(event, 'OUTLINED_BG_COLOR')}
              handleColorChange={(index, event) =>
                handleColorChange(index, event, 'OUTLINED_BG_COLOR')
              }
              isError={
                !!(
                  (touched as any).listingCardData?.template?.data
                    ?.outlinedButton?.borderColor &&
                  (errors as any).listingCardData?.template?.data
                    ?.outlinedButton?.borderColor
                )
              }
              errorText={
                !!(touched as any).listingCardData?.template?.data
                  ?.outlinedButton?.borderColor &&
                ((errors as any).listingCardData?.template?.data?.outlinedButton
                  ?.borderColor ||
                  '')
              }
              secondaryVariant={true}
            />
          </div>
        </div>
      </div>
    </div>
  )
}
export const generateOptions = (
  id: string,
  optionsArray: { label: string; value: string }[] | string[],
  title: string,
  placeHolder: string,
  humanReadableKey: string,
  defaultValue: string | undefined
): FormField => {
  return {
    title: title,
    placeHolder: placeHolder,
    id: id,
    isRequired: false,
    humanReadableKey: humanReadableKey,
    isReadOnly: false,
    isCustom: false,
    defaultValue: defaultValue,
    dataType: 'enum' as DataType,
    options: optionsArray,
  }
}
